import React, { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import ToggleButtonContainer, { ToggleOption } from '../../ToggleButtonContainer'

interface Props {
  flightsAvailable: boolean
  flights: boolean
  isMemberPrice?: boolean
  hotelTotalPrice: number
  hotelFlightTotalPrice?: number
  pricesLoading?: boolean
}

function BaseHotelFlightsToggleButton(props: Props) {
  const { flightsAvailable, flights, isMemberPrice, hotelTotalPrice, hotelFlightTotalPrice, pricesLoading } = props
  const dispatch = useOfferPageDispatch()
  const intl = useIntl()

  const noFlightsPrice = hotelTotalPrice === hotelFlightTotalPrice
  const disableFlights = !flightsAvailable || noFlightsPrice

  const handleOptionChange = useCallback((value: string) => {
    const isFlightsSelected = value === 'flights'
    dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected })
  }, [dispatch])

  useEffect(() => {
    if (!disableFlights && flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableFlights])

  const optionA: ToggleOption = useMemo(() => ({
    label: intl.formatMessage({
      id: 'b0pv8cJW9S',
      defaultMessage: 'Hotel',
      description: 'Label for hotel-only option in hotel/flights toggle button',
    }),
    value: 'hotel',
    displayPrice: hotelTotalPrice,
  }), [hotelTotalPrice, intl])

  const optionB: ToggleOption = useMemo(() => ({
    label: intl.formatMessage({
      id: '2NPAn/4P53',
      defaultMessage: 'Hotel + flights',
      description: 'Label for hotel with flights option in hotel/flights toggle button',
    }),
    value: 'flights',
    displayPrice: hotelFlightTotalPrice,
    buttonStartIcon: <LinePlaneDepartureIcon />,
  }), [hotelFlightTotalPrice, intl])

  const unavailableMessage = !flightsAvailable
    ? intl.formatMessage({
        id: 'gve3LglgVc',
        defaultMessage: 'Unavailable for your dates',
        description: 'Message shown when flights option is unavailable for selected dates',
      })
    : undefined

  return (
    <ToggleButtonContainer
      optionA={optionA}
      optionB={optionB}
      selectedValue={flights ? 'flights' : 'hotel'}
      unavailableMessage={unavailableMessage}
      isMemberPrice={isMemberPrice}
      pricesLoading={pricesLoading}
      disableOptionB={disableFlights}
      onOptionChange={handleOptionChange}
    />
  )
}

export default withOfferPageAndReduxState((state) => ({
  flights: state.flights,
}), BaseHotelFlightsToggleButton)
